import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/modules/common/api/index.ts';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import {
  BronIDGetVerificationStatusResponse,
  GetEntitiesListResponse,
  GetEntitiesListV1SrIssuerEntitiesGetParams,
  GetVerificationStatusV1SrBronidGetVerificationStatusGetParams,
} from 'api';

export const ISSUER_ENTITIES_QUERY_KEY = 'vestra/issuer-entities-query';
export const ISSUER_ENTITY_BRON_ID_KYC_QUERY_KEY = 'vestra/issuer-entity-bron-id-kyc';

export const useIssuerEntitiesQuery = (
  payload: GetEntitiesListV1SrIssuerEntitiesGetParams,
  disabled = false,
) => {
  const [params] = useSearchParams();
  const { page, ...data } = payload;
  return useInfiniteQuery<GetEntitiesListResponse>({
    queryKey: [
      ISSUER_ENTITIES_QUERY_KEY,
      JSON.stringify(data),
      params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '',
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getEntitiesListV1SrIssuerEntitiesGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    enabled: !disabled,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useIssuerEntityBronIdKycDetailsQuery = (
  payload: GetVerificationStatusV1SrBronidGetVerificationStatusGetParams,
  disabled = false,
) => {
  const [params] = useSearchParams();
  return useQuery<BronIDGetVerificationStatusResponse>({
    queryKey: [
      ISSUER_ENTITY_BRON_ID_KYC_QUERY_KEY,
      JSON.stringify(payload),
      params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '',
    ],
    retry: 0,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getVerificationStatusV1SrBronidGetVerificationStatusGet(
        payload,
      );
      return response.data;
    },
    enabled: !disabled,
  });
};
